import React, { useState, useEffect, useMemo } from 'react';
import './FreshClub.less';
import * as api from 'util/api';
import {
  ShoppingCartOutlined,
  ShoppingOutlined,
  TeamOutlined,
  UserAddOutlined,
  CloseOutlined,
  ReloadOutlined,
  DownloadOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import { FullPageLoader, StickyHeader, CustomTable } from 'components';
import STORES from 'config/stores';
import {
  Button,
  Select,
  Table,
  Modal,
  Tag,
  Input,
  Tooltip,
  Progress,
} from 'antd';
import { CSVDownload } from 'react-csv';
import dayjs from 'dayjs';
import {
  SIZE_OTHER,
  formatNumber,
  formatPercent,
  parseCanSize,
  sizeSortKey,
  CustomSpinner,
  MetricStat,
} from './shared';
import FreshClubHistory from './FreshClubHistory';

const { Column } = Table;
const { Search } = Input;

const TABLE_ID = 'onetimes-summary-table';
const TOP_PRODUCT_BARS = 8;
const CHARGE_DATE_BUCKETS = 6;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const { Option } = Select;

const rechargeConfigs = {
  GPNZ: {
    dashboard_link: 'https://lookerstudio.google.com/reporting/cb7fe12c-0edb-49b3-b603-7f2eadaab231/page/tEnnC',
    recharge_admin: 'garage-project-sp',
  },
  GPAU: {
    dashboard_link: 'https://lookerstudio.google.com/reporting/35e39418-56fb-4ab0-af25-fea69297590a/page/tEnnC',
    recharge_admin: 'garageproject-australia-sp',
  },
};

const FreshClub = ({ webstoreAdmin, defaultStore }) => {
  const [store, setStore] = useState(defaultStore);
  const [searchQuery, setSearchQuery] = useState('');

  const [data, setData] = useState([]);
  const [allCustomerData, setAllCustomerData] = useState([]);
  const [allSubscriptionData, setAllSubscriptionData] = useState([]);

  const [loading, setLoading] = useState(null);
  const [loadingCustomers, setLoadingCustomers] = useState(null);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [customerData, setCustomerData] = useState([]);
  const [sizeFilter, setSizeFilter] = useState(null); // null = all sizes

  const [downloadCsv, setdownloadCsv] = useState(false);

  const metrics = data.metrics || {};
  const {
    totalCansReserved,
    uniqueCustomers,
    totalValue,
  } = metrics;

  const memberCount = useMemo(() => {
    if (!Array.isArray(allCustomerData)) return null;
    return allCustomerData.filter((customer) => customer?.subscriptionsActiveCount >= 1).length;
  }, [allCustomerData]);

  const reservedCans = useMemo(() => {
    if (!Array.isArray(allSubscriptionData)) return null;
    return allSubscriptionData.reduce((sum, subscription) => {
      const quantity = parseInt(subscription.variantQuantity, 10);
      return Number.isNaN(quantity) ? sum : sum + quantity;
    }, 0);
  }, [allSubscriptionData]);

  const productRows = useMemo(() => {
    const totalUnits = totalCansReserved || 0;
    if (Array.isArray(data?.processedData) && data.processedData.length > 0) {
      const groups = new Map();
      data.processedData.forEach((ot) => {
        const productId = ot.externalProductId?.ecommerce;
        const productName = ot.productTitle || 'Unknown';
        const size = parseCanSize(ot.variantTitle, ot.productTitle);
        const key = `${productId}::${size}`;
        const existing = groups.get(key);
        if (existing) {
          existing.unitCount += ot.unitCount || 0;
          existing.quantity += Number(ot.quantity) || 0;
        } else {
          groups.set(key, {
            key,
            productId,
            productName,
            size,
            unitCount: ot.unitCount || 0,
            quantity: Number(ot.quantity) || 0,
          });
        }
      });
      return Array.from(groups.values())
        .map((row) => ({
          ...row,
          sharePct: totalUnits > 0 ? (row.unitCount / totalUnits) * 100 : 0,
        }))
        .sort((a, b) => {
          const sizeCmp = sizeSortKey(a.size) - sizeSortKey(b.size);
          if (sizeCmp !== 0) return sizeCmp;
          return b.unitCount - a.unitCount;
        });
    }
    if (!data?.productSummary) return null;
    return data.productSummary.map((product) => ({
      ...product,
      key: product.productId,
      size: parseCanSize(product.productName),
      sharePct: totalUnits > 0 ? (product.unitCount / totalUnits) * 100 : 0,
    }));
  }, [data?.processedData, data?.productSummary, totalCansReserved]);

  const sizeOptions = useMemo(() => {
    if (!productRows?.length) return [];
    const counts = {};
    productRows.forEach((row) => {
      counts[row.size] = (counts[row.size] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([size, count]) => ({ size, count }))
      .sort((a, b) => sizeSortKey(a.size) - sizeSortKey(b.size));
  }, [productRows]);

  const tableDataSource = useMemo(() => {
    if (!productRows) return null;
    let rows = productRows;
    if (sizeFilter) {
      rows = rows.filter((row) => row.size === sizeFilter);
    }
    const query = searchQuery.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) => (
      String(row.productName || '').toLowerCase().includes(query)
      || String(row.size || '').toLowerCase().includes(query)
      || String(row.unitCount ?? '').toLowerCase().includes(query)
    ));
  }, [productRows, searchQuery, sizeFilter]);

  const packBreakdown = useMemo(() => {
    if (!allSubscriptionData || !allSubscriptionData.length) return null;
    const addressesWithOnetimes = new Set();
    if (data?.processedData) {
      data.processedData.forEach((ot) => {
        if (ot.addressId) addressesWithOnetimes.add(ot.addressId);
      });
    }
    const groups = {};
    allSubscriptionData.forEach((sub) => {
      const label = sub.variantTitle || 'Unknown';
      if (!groups[label]) {
        groups[label] = { total: 0, withOnetimes: 0, withoutOnetimes: 0 };
      }
      groups[label].total += 1;
      if (addressesWithOnetimes.has(sub.addressId)) {
        groups[label].withOnetimes += 1;
      } else {
        groups[label].withoutOnetimes += 1;
      }
    });
    const rows = Object.entries(groups)
      .map(([variant, counts]) => ({ key: variant, variant, ...counts }))
      .sort((a, b) => b.total - a.total);
    const totalSubs = rows.reduce((sum, row) => sum + row.total, 0);
    const totalWith = rows.reduce((sum, row) => sum + row.withOnetimes, 0);
    const totalWithout = rows.reduce((sum, row) => sum + row.withoutOnetimes, 0);
    return {
      rows,
      totalSubs,
      totalWith,
      totalWithout,
    };
  }, [allSubscriptionData, data?.processedData]);

  const saleMix = useMemo(() => {
    const metricsReady = data?.metrics != null;
    const membersReady = memberCount != null;
    if (!metricsReady && !membersReady) return null;

    const addOnRate = (membersReady && memberCount > 0 && uniqueCustomers != null)
      ? (uniqueCustomers / memberCount) * 100
      : null;

    const avgCans = (uniqueCustomers > 0 && totalCansReserved != null)
      ? totalCansReserved / uniqueCustomers
      : null;

    const avgValue = (uniqueCustomers > 0 && totalValue != null)
      ? totalValue / uniqueCustomers
      : null;

    const totalUnits = totalCansReserved || 0;
    const topProducts = (data?.productSummary || [])
      .filter((product) => product.unitCount > 0)
      .sort((a, b) => b.unitCount - a.unitCount)
      .slice(0, TOP_PRODUCT_BARS)
      .map((product) => ({
        key: product.productId,
        name: product.productName,
        units: product.unitCount,
        pct: totalUnits > 0 ? (product.unitCount / totalUnits) * 100 : 0,
      }));

    const chargeBuckets = {};
    (data?.processedData || []).forEach((ot) => {
      const raw = ot.nextChargeScheduledAt;
      if (!raw) return;
      const dateKey = dayjs(raw).format('YYYY-MM-DD');
      if (!dayjs(dateKey).isValid() || !ot.unitCount) return;
      chargeBuckets[dateKey] = (chargeBuckets[dateKey] || 0) + ot.unitCount;
    });
    const chargeDates = Object.entries(chargeBuckets)
      .map(([date, units]) => ({ key: date, date, units }))
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(0, CHARGE_DATE_BUCKETS);

    return {
      addOnRate,
      avgCans,
      avgValue,
      topProducts,
      chargeDates,
      ready: metricsReady,
    };
  }, [
    data?.metrics,
    data?.productSummary,
    data?.processedData,
    memberCount,
    uniqueCustomers,
    totalCansReserved,
    totalValue,
  ]);

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const fetchData = () => {
    setLoading(true);
    console.log('Fetching onetimes...');
    api.getAllOnetimesData(STORES[store])
      .then((response) => {
        console.log('...Fetched onetimes');
        setData(response);
      })
      .catch((error) => console.error('Error fetching data:', error))
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchCustomerData = () => {
    setLoadingCustomers(true);
    console.log('Fetching customer data...');
    api.getAllCustomerData(STORES[store])
      .then((response) => {
        console.log('...Fetched customer data');
        setAllCustomerData(Array.isArray(response) ? response : []);
      })
      .catch((error) => {
        console.error('Error fetching customer data:', error);
        setAllCustomerData([]);
      })
      .finally(() => {
        setLoadingCustomers(false);
      });
  };

  const fetchSubscriptionData = () => {
    setLoadingSubscriptions(true);
    console.log('Fetching subscription data...');
    api.getAllSubscriptionData(STORES[store])
      .then((response) => {
        console.log('...Fetched subscription data');
        setAllSubscriptionData(Array.isArray(response) ? response : []);
      })
      .catch((error) => {
        console.error('Error fetching subscription data:', error);
        setAllSubscriptionData([]);
      })
      .finally(() => {
        setLoadingSubscriptions(false);
      });
  };

  useEffect(() => {
    setAllCustomerData(null);
    setAllSubscriptionData(null);
    setSizeFilter(null);
    fetchData();
    fetchCustomerData();
    fetchSubscriptionData();
  }, [store]);

  const handleDownload = React.useCallback(() => {
    setdownloadCsv(true);
    setTimeout(() => setdownloadCsv(false), 1000);
  }, []);

  if (!data) {
    return <FullPageLoader />;
  }

  const findCustomersByProductId = (onetimesData, productId, size) => {
    const customerAggregationMap = new Map();

    onetimesData.forEach((onetime) => {
      if (onetime.externalProductId.ecommerce !== productId) return;
      const onetimeSize = parseCanSize(onetime.variantTitle, onetime.productTitle);
      if (size && onetimeSize !== size) return;

      const { customerId } = onetime;
      const customerObject = allCustomerData?.find((customer) => customer.id === customerId);

      if (customerAggregationMap.has(customerId)) {
        const existingEntry = customerAggregationMap.get(customerId);
        customerAggregationMap.set(customerId, {
          ...existingEntry,
          unitCount: existingEntry.unitCount + onetime.unitCount,
        });
      } else {
        customerAggregationMap.set(customerId, {
          onetimesId: onetime.id,
          productId,
          customerId,
          unitCount: onetime.unitCount,
          customerUrl: `https://${rechargeConfigs[store].recharge_admin}.admin.rechargeapps.com/merchant/customers/${customerId}`,
          customerName: `${customerObject?.firstName} ${customerObject?.lastName}`,
          customerObject,
        });
      }
    });

    return Array.from(customerAggregationMap.values());
  };

  const lookerLink = rechargeConfigs[store]?.dashboard_link;
  const saleMixLoading = !!loading
    || !!loadingCustomers
    || data?.metrics == null
    || !Array.isArray(allCustomerData);

  return (
    <div id="fresh-club-container">
      <StickyHeader id="fresh-club-header">
        <Select
          defaultValue={defaultStore}
          style={{ width: 180 }}
          value={store}
          disabled={loading || loadingCustomers}
          onChange={(newStore) => {
            setStore(STORES[newStore]);
          }}
        >
          <Option value={STORES.GPNZ}>Garage Project NZ</Option>
          <Option value={STORES.GPAU}>Garage Project AU</Option>
        </Select>
        <Button
          id="dowload-csv"
          type="secondary"
          icon={<DownloadOutlined />}
          onClick={handleDownload}
        >
          Download CSV
        </Button>
        {lookerLink && (
          <Tooltip title="Open Looker Studio report">
            <Button
              type="secondary"
              icon={<LineChartOutlined />}
              href={lookerLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              Looker report
            </Button>
          </Tooltip>
        )}
        <Search
          className="fresh-club-header-search"
          placeholder="Search for onetimes..."
          allowClear
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          disabled={!productRows}
        />
        <Tooltip title={!searchQuery ? 'There is no search term' : null}>
          <Button
            type="primary"
            icon={<CloseOutlined />}
            onClick={() => setSearchQuery('')}
            disabled={!searchQuery}
          >
            Clear search
          </Button>
        </Tooltip>
        <div className="fresh-club-refresh-actions">
          <Tooltip title="Reload onetimes from Recharge">
            <Button
              id="recharge-data-refresh-button"
              type="text"
              size="small"
              icon={<ReloadOutlined spin={!!loading} />}
              onClick={fetchData}
              disabled={loading}
            >
              Onetimes
            </Button>
          </Tooltip>
          <Tooltip title="Reload customers from Recharge">
            <Button
              id="recharge-customer-refresh-button"
              type="text"
              size="small"
              icon={<ReloadOutlined spin={!!loadingCustomers} />}
              onClick={fetchCustomerData}
              disabled={loadingCustomers}
            >
              Customers
            </Button>
          </Tooltip>
        </div>
        {downloadCsv
          && productRows
          && (
            <CSVDownload
              data={productRows}
              headers={[
                {
                  label: 'Product Name',
                  key: 'productName',
                },
                {
                  label: 'Size',
                  key: 'size',
                },
                {
                  label: 'Unit Count',
                  key: 'unitCount',
                },
                {
                  label: 'Product ID',
                  key: 'productId',
                },
              ]}
              target="_blank"
              rel="noopener noreferrer"
            />
          )}
      </StickyHeader>
      <div id="fresh-club-body">
        <div id="onetimes-metrics-container">
          <MetricStat
            label="Upcoming onetime cans"
            icon={<ShoppingCartOutlined />}
            loading={!!loading || data?.metrics == null}
            value={formatNumber(totalCansReserved)}
          />
          <MetricStat
            label="Upcoming onetime value"
            icon={<ShoppingOutlined />}
            loading={!!loading || data?.metrics == null}
            value={`$${formatNumber(totalValue)}`}
          />
          <MetricStat
            label="Customers with upcoming onetimes"
            icon={<UserAddOutlined />}
            loading={!!loading || data?.metrics == null}
            value={formatNumber(uniqueCustomers)}
          />
          <MetricStat
            label="FRESH Club members"
            icon={<TeamOutlined />}
            loading={!!loadingCustomers || !Array.isArray(allCustomerData)}
            value={formatNumber(memberCount)}
          />
          <MetricStat
            label="FRESH cans reserved"
            icon={<TeamOutlined />}
            loading={!!loadingSubscriptions || !Array.isArray(allSubscriptionData)}
            value={formatNumber(reservedCans)}
          />
        </div>

        <div id="fresh-club-panels">
          <div id="fresh-club-insights">
            <div className="fresh-club-panel pack-breakdown-panel">
              <div className="fresh-club-panel-title">Pack breakdown</div>
              {(loadingSubscriptions || !Array.isArray(allSubscriptionData)) && (
                <div className="pack-breakdown-loading">
                  <CustomSpinner fontSize={18} />
                </div>
              )}
              {!loadingSubscriptions && Array.isArray(allSubscriptionData) && !packBreakdown && (
                <p className="pack-breakdown-empty">
                  No active FRESH Club subscriptions loaded.
                </p>
              )}
              {packBreakdown && (
                <>
                  <div className="pack-breakdown-summary">
                    <Tag color="blue">
                      {`${formatNumber(packBreakdown.totalSubs)} total`}
                    </Tag>
                    <Tag color="green">
                      {`${formatNumber(packBreakdown.totalWithout)} pre-packable`}
                    </Tag>
                    <Tag color="orange">
                      {`${formatNumber(packBreakdown.totalWith)} with add-ons`}
                    </Tag>
                  </div>
                  <Table
                    className="pack-breakdown-table"
                    dataSource={packBreakdown.rows}
                    pagination={false}
                    size="small"
                  >
                    <Column
                      title="Variant"
                      dataIndex="variant"
                      key="variant"
                    />
                    <Column
                      title="Total"
                      dataIndex="total"
                      key="total"
                      width={64}
                      align="right"
                    />
                    <Column
                      title="Pre-pack"
                      dataIndex="withoutOnetimes"
                      key="withoutOnetimes"
                      width={72}
                      align="right"
                      render={(val) => (
                        <Tag color="green">{val}</Tag>
                      )}
                    />
                    <Column
                      title="Add-ons"
                      dataIndex="withOnetimes"
                      key="withOnetimes"
                      width={72}
                      align="right"
                      render={(val) => (
                        <Tag color="orange">{val}</Tag>
                      )}
                    />
                  </Table>
                </>
              )}
            </div>

            <div className="fresh-club-panel sale-mix-panel">
              <div className="fresh-club-panel-title">Sale mix</div>
              {saleMixLoading && (
                <div className="sale-mix-loading">
                  <CustomSpinner fontSize={18} />
                </div>
              )}
              {!saleMixLoading && saleMix && (
                <>
                  <div className="sale-mix-section">
                    <div className="sale-mix-section-label">Add-on rate</div>
                    {saleMix.addOnRate != null ? (
                      <Progress
                        percent={Math.min(100, Math.round(saleMix.addOnRate))}
                        format={() => formatPercent(saleMix.addOnRate)}
                        status="active"
                      />
                    ) : (
                      <p className="sale-mix-empty">Need member + onetime counts.</p>
                    )}
                    <div className="sale-mix-stats">
                      <div className="sale-mix-stat">
                        <div className="sale-mix-stat-value">
                          {saleMix.avgCans != null
                            ? formatNumber(Math.round(saleMix.avgCans * 10) / 10)
                            : '—'}
                        </div>
                        <div className="sale-mix-stat-label">Avg cans / add-on cust.</div>
                      </div>
                      <div className="sale-mix-stat">
                        <div className="sale-mix-stat-value">
                          {saleMix.avgValue != null
                            ? `$${formatNumber(Math.round(saleMix.avgValue * 100) / 100)}`
                            : '—'}
                        </div>
                        <div className="sale-mix-stat-label">Avg $ / add-on cust.</div>
                      </div>
                    </div>
                  </div>

                  <div className="sale-mix-section">
                    <div className="sale-mix-section-label">Top products</div>
                    {saleMix.topProducts.length === 0 ? (
                      <p className="sale-mix-empty">No onetime products yet.</p>
                    ) : (
                      saleMix.topProducts.map((product) => (
                        <div className="sale-mix-bar-row" key={product.key}>
                          <div>
                            <div className="sale-mix-bar-label" title={product.name}>
                              {product.name}
                            </div>
                            <div className="sale-mix-bar-track">
                              <div
                                className="sale-mix-bar-fill"
                                style={{ width: `${Math.min(100, product.pct)}%` }}
                              />
                            </div>
                          </div>
                          <div className="sale-mix-bar-pct">
                            {formatPercent(product.pct)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="sale-mix-section">
                    <div className="sale-mix-section-label">Upcoming charge dates</div>
                    {saleMix.chargeDates.length === 0 ? (
                      <p className="sale-mix-empty">No charge dates on loaded onetimes.</p>
                    ) : (
                      <div className="sale-mix-charge-list">
                        {saleMix.chargeDates.map((bucket) => (
                          <div className="sale-mix-charge-row" key={bucket.key}>
                            <span className="sale-mix-charge-date">
                              {dayjs(bucket.date).format('D MMM YYYY')}
                            </span>
                            <span className="sale-mix-charge-count">
                              {`${formatNumber(bucket.units)} cans`}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <div id="onetimes-summary-panel" className="fresh-club-panel">
            <div className="onetimes-panel-header">
              <div className="fresh-club-panel-title">Upcoming onetime products</div>
              {sizeOptions.length > 0 && (
                <div className="onetimes-size-filters">
                  <Tag
                    color={!sizeFilter ? 'blue' : 'default'}
                    onClick={() => setSizeFilter(null)}
                  >
                    {`All (${productRows?.length || 0})`}
                  </Tag>
                  {sizeOptions.map(({ size, count }) => (
                    <Tag
                      key={size}
                      color={sizeFilter === size ? 'blue' : 'default'}
                      onClick={() => setSizeFilter(sizeFilter === size ? null : size)}
                    >
                      {`${size} (${count})`}
                    </Tag>
                  ))}
                </div>
              )}
            </div>
            <CustomTable
              id={TABLE_ID}
              pluralLabel="onetimes"
              disableControls
              customRowClick={(record) => {
                if (!data.processedData || !allCustomerData) {
                  console.error('Waiting for data');
                }
                const customers = findCustomersByProductId(
                  data.processedData,
                  record.productId,
                  record.size,
                );
                setCustomerData(null);
                delay(1000);
                setCustomerData({
                  name: record.size && record.size !== SIZE_OTHER
                    ? `${record.productName} (${record.size})`
                    : record.productName,
                  product: record.productId,
                  customers,
                });
                setModalOpen(true);
              }}
              pagination={false}
              dataSource={tableDataSource}
            >
              <Column
                title="Product Name"
                dataIndex="productName"
                key="productName"
              />
              <Column
                title="Size"
                dataIndex="size"
                key="size"
                sortType="string"
                width={100}
                render={(val) => (
                  <Tag color={val === SIZE_OTHER ? 'default' : 'geekblue'}>{val}</Tag>
                )}
              />
              <Column
                title="Cans"
                dataIndex="unitCount"
                key="unitCount"
                sortType="number"
                sortDirections={['descend', 'ascend', 'descend']}
                width={110}
                render={(val, row) => (val > 0 || row.quantity == null
                  ? formatNumber(val)
                  : `${formatNumber(row.quantity)} ${row.quantity === 1 ? 'item' : 'items'}`)}
              />
              <Column
                title="Share"
                dataIndex="sharePct"
                key="sharePct"
                sortType="number"
                sortDirections={['descend', 'ascend', 'descend']}
                width={90}
                align="right"
                render={(val) => formatPercent(val)}
              />
            </CustomTable>
          </div>

          <FreshClubHistory store={store} />
        </div>
      </div>
      <Modal
        title={`Customers with upcoming ${customerData.name}`}
        visible={modalOpen}
        onCancel={handleModalClose}
      >
        {customerData.customers?.map((customer) => (
          <div id="customer-row" key={customer.customerId}>
            {console.log(customer)}
            <p>
              {customer.customerName}
              :&nbsp;
              {customer.unitCount}
              {customer.unitCount === 1 ? 'x unit ( ID:' : 'x units ( ID:'}
              &nbsp;
            </p>
            <p>
              <a href={customer.customerUrl} target="blank">
                {customer.customerId}
              </a>
              &nbsp; )
            </p>
          </div>
        ))}
      </Modal>
    </div>
  );
};

export default FreshClub;
